import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BaxterDog } from './BaxterDog';
import { Starfield } from './Starfield';
import { SWIRL_EYE_Y, Swirl, reducedMotion } from './Swirl';
import {
  BAXTER_STREAM_EVENT,
  MAKES,
  PHASES,
  hintOf,
  paletteVars,
  phaseOf,
  type BaxterStreamDetail,
  type MagicRequest,
} from './magic';

/** Cards in the air at once; a burst of writes beyond this simply drops its extras. */
const MAX_FLIGHTS = 6;

interface Flight { id: string; label: string; twice: boolean; alt: boolean; kind: 'photo' | 'code' | 'post' | 'page'; target: string }

/** Where each theme file lands on the site panel (a `data-land` key); the rest land on the post card or the panel. */
const THEME_TARGET: Record<string, string> = {
  'static/style.css': 'style', 'templates/base.html': 'desc', 'templates/partials/header.html': 'header',
  'templates/partials/footer.html': 'footer', 'templates/index.html': 'title', 'templates/page.html': 'nav',
  'templates/post.html': 'post', 'templates/archive.html': 'post', 'templates/partials/post_card.html': 'post',
  'templates/tag.html': 'post', 'templates/tags.html': 'post',
};
type Status = 'working' | 'ready' | 'question' | 'approval' | 'error';

/**
 * "Baxter's magic": the full-screen loader the New Site wizard hands over to.
 * Baxter works in the chat panel underneath; this listens to the same stream
 * (BAXTER_STREAM_EVENT) and turns the tool wall into five calm stages, the
 * site drawing itself, the real palette it wrote, and the pages as they land.
 */
export function BaxterMagic({ siteSlug, request, onClose }: { siteSlug: string; request: MagicRequest; onClose: () => void }) {
  const calls = useRef(new Map<string, { name: string; args: Record<string, unknown> }>());
  const flightSeq = useRef(0);
  const photoSeq = useRef(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [phase, setPhase] = useState(0);
  const [hint, setHint] = useState('Getting started');
  const [agent, setAgent] = useState<string | null>(null);
  /** Baxter's crew members at work right now, by id → role. */
  const [crew, setCrew] = useState<Record<string, string>>({});
  const [made, setMade] = useState<number[]>(() => PHASES.map(() => 0));
  const [pages, setPages] = useState<Array<{ title: string; done: boolean }>>([]);
  const [photos, setPhotos] = useState(0);
  const [vars, setVars] = useState<Array<{ name: string; hex: string }>>([]);
  const [title, setTitle] = useState(request.name?.trim() || '');
  const [description, setDescription] = useState('');
  const [captions, setCaptions] = useState<string[]>([]);
  const [files, setFiles] = useState<string[]>([]);
  const [post, setPost] = useState<string | null>(null);
  const [forked, setForked] = useState(false);
  const [named, setNamed] = useState(false);
  const [flights, setFlights] = useState<Flight[]>([]);
  const [burst, setBurst] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('working');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const onStream = (e: Event) => {
      const { siteSlug: slug, event, data } = (e as CustomEvent<BaxterStreamDetail>).detail;
      if (slug !== siteSlug) return;
      switch (event) {
        case 'tool_start': {
          const args = (data.args ?? {}) as Record<string, unknown>;
          calls.current.set(data.id, { name: data.name, args });
          const at = phaseOf(data.name);
          if (at >= 0) setPhase((p) => Math.max(p, at)); // the rail only moves forward
          setHint(hintOf(data.name, args));
          if (data.name === 'wpp_create_content' && args.content_type === 'pages' && typeof args.title === 'string') {
            const title = args.title;
            setPages((list) => (list.some((p) => p.title === title) ? list : [...list, { title, done: false }]));
          }
          // Cards only fly where the site panel shows (lg+); hidden ones never end.
          if (MAKES.has(data.name) && window.matchMedia('(min-width: 64rem)').matches) {
            const kind = data.name === 'wpp_import_media' ? 'photo' : data.name === 'wpp_write_theme_file' ? 'code'
              : args.content_type === 'posts' ? 'post' : 'page';
            const label = String(args.title ?? (args.path ? String(args.path).replace(/\/$/, '').split('/').pop() : null) ?? args.query ?? 'Photo');
            const target = kind === 'photo' ? `photo-${photoSeq.current++ % 2}` : kind === 'code' ? THEME_TARGET[String(args.path)] ?? 'site'
              : kind === 'post' ? 'post' : `page:${String(args.title ?? '')}`;
            setFlights((list) => (list.length >= MAX_FLIGHTS ? list
              : [...list, { id: `${data.id}-${flightSeq.current++}`, label, kind, target, alt: flightSeq.current % 2 === 1, twice: flightSeq.current % 3 === 0 }]));
          }
          break;
        }
        case 'tool_result': {
          const call = calls.current.get(data.id);
          calls.current.delete(data.id);
          if (data.preview_url) setPreview(data.preview_url);
          if (!call) break;
          if (!data.ok) { setHint(hintOf(call.name, call.args, false)); break; }
          const at = phaseOf(call.name);
          if (at >= 0) setMade((m) => m.map((n, i) => (i === at ? n + 1 : n)));
          if (MAKES.has(call.name)) setBurst((b) => b + 1);
          const a = call.args;
          if (call.name === 'wpp_update_settings') {
            setNamed(true);
            if (typeof a.title === 'string' && a.title) setTitle(a.title);
            if (typeof a.description === 'string' && a.description) setDescription(a.description);
          }
          if (call.name === 'wpp_fork_theme') setForked(true);
          if (call.name === 'wpp_import_media') {
            setPhotos((n) => n + (Array.isArray(a.urls) ? a.urls.length : 1));
            if (typeof a.query === 'string') setCaptions((list) => [...list, a.query as string]);
          }
          if (call.name === 'wpp_create_content') {
            if (a.content_type === 'posts') { if (typeof a.title === 'string') setPost(a.title); }
            else setPages((list) => list.map((p) => (p.title === a.title ? { ...p, done: true } : p)));
          }
          if (call.name === 'wpp_write_theme_file' && typeof a.path === 'string') {
            const file = a.path.split('/').at(-1) ?? a.path;
            setFiles((list) => (list.includes(file) ? list : [...list, file]));
            if (a.path === 'static/style.css' && typeof a.content === 'string') {
              const found = paletteVars(a.content);
              if (found.length) setVars(found);
            }
          }
          break;
        }
        case 'model':
          if (typeof data.model === 'string' && data.model.endsWith('Sub agent')) setAgent(data.model);
          break;
        case 'crew':
          setAgent(null); // the lead's chip would otherwise pop back when the last member ends
          setCrew((c) => {
            const next = { ...c };
            if (data.state === 'start') next[data.agent] = data.role; else delete next[data.agent];
            return next;
          });
          break;
        case 'approval_required':
          setStatus('approval');
          break;
        case 'error':
          setStatus('error');
          setMessage(data.message ?? '');
          break;
        case 'done':
        case 'end':
          setStatus((s) => (s !== 'working' ? s : 'question'));
          break;
      }
    };
    window.addEventListener(BAXTER_STREAM_EVENT, onStream);
    return () => window.removeEventListener(BAXTER_STREAM_EVENT, onStream);
  }, [siteSlug]);

  // A turn that built the site is "ready" as soon as it ends; one that only talked has a question.
  const built = made[PHASES.length - 1] > 0;
  const shown: Status = status === 'question' && (built || preview) ? 'ready' : status;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A real modal: everything behind it is inert and unscrollable while it shows.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const behind = [...document.body.children].filter((el) => el !== rootRef.current) as HTMLElement[];
    const wasInert = behind.map((el) => el.inert);
    behind.forEach((el) => { el.inert = true; });
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Tool toasts ("Theme Updated: …") belong to the chat, not this screen.
    document.documentElement.setAttribute('data-baxter-magic', '');
    return () => {
      document.documentElement.removeAttribute('data-baxter-magic');
      behind.forEach((el, i) => { el.inert = wasInert[i]; });
      document.body.style.overflow = overflow;
    };
  }, []);

  const role = (...names: string[]) => vars.find((v) => names.includes(v.name))?.hex;
  const bg = role('bg', 'background', 'paper') ?? vars[0]?.hex;
  const ink = role('fg', 'text', 'ink', 'foreground') ?? vars[1]?.hex;
  const accent = role('accent', 'primary', 'brand') ?? vars[2]?.hex;
  const link = role('link') ?? accent;
  const styled = vars.length > 0;
  // Like 07, the theme lands in order: the palette, then base.html tints the page, then header.html links it.
  const painted = styled && (files.includes('base.html') || phase > 2);
  const linked = styled && (files.includes('header.html') || phase > 2);
  const siteName = title || 'Your site';
  const donePages = pages.filter((p) => p.done);
  // One bar for the whole run: finished stages plus a share of the current one.
  const progress = shown === 'ready' ? 100 : Math.min(98, ((phase + Math.min(0.9, made[phase] / 8)) / PHASES.length) * 100);
  const summary = (i: number) => [
    named ? 'Named the site' : '',
    photos ? `${photos} photo${photos === 1 ? '' : 's'}` : '',
    files.length ? `${files.length} file${files.length === 1 ? '' : 's'}` : '',
    made[3] ? `${made[3]} made` : '',
    built ? 'Built' : '',
  ][i];

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Baxter is building your site"
      data-testid="baxter-magic"
      data-status={shown}
      className="magic-enter fixed inset-0 z-[70] overflow-x-hidden overflow-y-auto bg-[#05060b] text-white"
    >
      <Starfield twinkle />

      <div ref={wrapRef} className="relative mx-auto flex min-h-full max-w-6xl flex-col px-4 pb-6 pt-4">
        {/* What Baxter makes leaves the eye, circles the site and lands on the spot it changes. */}
        <div className="pointer-events-none absolute inset-0 z-10" aria-hidden="true">
          {flights.map((f) => (
            <FlyCard key={f.id} f={f} wrap={wrapRef} onDone={() => setFlights((list) => list.filter((x) => x.id !== f.id))} />
          ))}
        </div>
        {/* Edge to edge, like concept 07: the well fades out at the sides and bottom. */}
        <div className={cn('relative left-1/2 h-[26vh] min-h-44 w-screen -translate-x-1/2 transition-opacity duration-(--duration-drawer) ease-out', shown === 'ready' && 'opacity-40')}>
          <Swirl />
          <div data-land="eye" className="absolute left-1/2 -translate-x-1/2 -translate-y-[70%]" style={{ top: SWIRL_EYE_Y }}>
            <WizardHat tip={shown === 'ready'} />
            <BaxterDog activity={shown === 'ready' ? 'Jumping' : 'Resting'} className="size-10 text-amber-400 drop-shadow-[0_0_12px_rgba(245,180,0,0.35)]" />
            {burst > 0 && <Sparkles key={burst} />}
          </div>
        </div>
        <div className="text-center lg:absolute lg:left-8 lg:top-6 lg:max-w-xs lg:text-left">
          <h1 className="text-lg font-semibold">
            {shown === 'ready' ? 'Your private preview is ready' : shown === 'question' ? 'Baxter has a question for you' : shown === 'approval' ? 'Baxter needs your OK' : shown === 'error' ? 'Baxter hit a snag' : `Baxter is building ${siteName}`}
          </h1>
          <p className="mt-1 text-xs text-white/55">
            {shown === 'working' ? 'This takes a few minutes. Your preview opens here.' : shown === 'error' ? message || 'Open the chat to see what happened.' : shown === 'ready' ? 'Every page is written and the preview is built.' : 'Open the chat to answer.'}
          </p>
        </div>
        {/* Screen readers hear the five stages and the end, not every tool call. */}
        <p role="status" className="sr-only">{shown === 'working' ? PHASES[phase].label : ''}</p>

        <div className="mt-4 grid gap-4 lg:grid-cols-[15rem_1fr_15rem]">
          {/* Steps */}
          <div className={cn('self-start rounded-xl border border-white/10 bg-white/[0.03] p-3 transition-opacity duration-(--duration-drawer) ease-out', shown === 'ready' && 'lg:opacity-50')}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[11px] tracking-wide text-white/50 uppercase">Steps</span>
              {shown === 'working' && (Object.keys(crew).length ? (
                <span className="flex min-w-0 flex-nowrap justify-end gap-1 overflow-hidden" aria-label="Baxter's crew at work">
                  {Object.entries(crew).map(([id, role]) => (
                    <span key={id} className="magic-land flex min-w-0 items-center gap-1.5 rounded-full border border-white/15 px-2 py-0.5 text-[11px] text-white/70">
                      <span className="size-1.5 shrink-0 rounded-full bg-amber-400" /><span className="truncate">{role.replace(/ Sub agent$/, '')}</span>
                    </span>
                  ))}
                </span>
              ) : agent && (
                <span className="flex items-center gap-1.5 rounded-full border border-white/15 px-2 py-0.5 text-[11px] text-white/70">
                  <span className="size-1.5 rounded-full bg-amber-400" />{agent}
                </span>
              ))}
            </div>
            <ol className="space-y-0.5" aria-label="Progress">
              {PHASES.map((p, i) => {
                const state = shown === 'ready' || i < phase ? 'done' : i === phase ? 'now' : 'next';
                return (
                  <li key={p.key} className={cn('rounded-md px-1.5 py-1.5 text-sm', state === 'next' && 'text-white/50')}>
                    <div className="flex items-center gap-2">
                      <span className={cn('flex size-4 shrink-0 items-center justify-center rounded-full', state === 'done' ? 'bg-emerald-500 text-[#05060b]' : state === 'now' ? 'magic-pulse' : 'border border-white/20')}>
                        {state === 'done' ? <Check className="size-3" strokeWidth={3} /> : state === 'now' ? <span className="size-1.5 rounded-full bg-amber-400" /> : null}
                      </span>
                      <span className="flex-1">{p.label}</span>
                      {state === 'done' && summary(i) && <span className="text-[11px] text-white/55">{summary(i)}</span>}
                    </div>
                    {state === 'now' && shown === 'working' && (
                      <div className="magic-land ml-6 mt-1.5 rounded-md bg-white/[0.05] px-2 py-1.5">
                        <p className="truncate text-xs text-white/80">{hint}</p>
                        {made[i] > 0 && <p className="text-[11px] tabular-nums text-white/55">{made[i]} made</p>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
            <p className="mt-2 flex items-center gap-1.5 px-1.5 text-[11px] text-white/55">
              <span className="size-1.5 rounded-full bg-amber-400" />
              {request.style?.trim() ? 'Using your colours, photos and fonts' : 'Baxter picks your style'}
            </p>
            <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/10">
              <div className="h-full w-full rounded-full bg-amber-400 transition-transform duration-(--duration-step) ease-out" style={{ transform: `translateX(-${100 - progress}%)` }} />
            </div>
          </div>

          {/* The site drawing itself */}
          <div className="relative hidden lg:block">
            <div
              data-land="site"
              className="overflow-hidden rounded-xl border border-white/10 shadow-2xl transition-colors duration-(--duration-drawer) ease-out"
              style={{ backgroundColor: painted && bg ? bg : 'rgba(255,255,255,0.04)', color: painted && ink ? ink : 'rgba(255,255,255,0.7)' }}
            >
              <div className="flex items-center gap-1.5 border-b border-current/10 px-3 py-2">
                {[0, 1, 2].map((d) => <span key={d} className="size-2 rounded-full bg-current opacity-25" />)}
              </div>
              <div className="px-5 pb-4 pt-3">
                <div data-land="header" className="relative flex items-center gap-3 pb-2">
                  {linked && accent && <span className="magic-wipe absolute inset-x-0 bottom-0 h-0.5" style={{ backgroundColor: accent }} />}
                  <span className="font-semibold" style={painted ? { fontFamily: 'ui-serif, Georgia, serif' } : undefined}>{title ? siteName : <span className="inline-block h-3 w-20 rounded bg-current opacity-15 align-middle" />}</span>
                  <nav data-land="nav" className="ml-auto flex flex-wrap justify-end gap-x-3 gap-y-1 text-[11px]">
                    {pages.length === 0 && [0, 1, 2, 3].map((d) => <span key={d} className="h-2.5 w-10 rounded bg-current opacity-10" />)}
                    {pages.length > 0 && <span style={linked ? { color: link } : undefined}>Home</span>}
                    {pages.map((p) => (
                      <span key={p.title} data-land={`page:${p.title}`} className={cn('magic-land', !p.done && 'opacity-40')} style={p.done && linked ? { color: link } : undefined}>{p.title}</span>
                    ))}
                    {post && <span style={linked ? { color: link } : undefined}>Blog</span>}
                  </nav>
                </div>
                {painted ? (
                  <h2 data-land="title" className="magic-land mt-4 text-3xl font-semibold" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>{siteName}</h2>
                ) : title ? <h2 data-land="title" className="mt-4 text-3xl font-semibold opacity-60">{siteName}</h2> : <div data-land="title" className="mt-4 h-6 w-1/2 rounded bg-current opacity-15" />}
                {description ? <p data-land="desc" className={cn('magic-land mt-2 max-w-md text-xs transition-opacity duration-(--duration-dialog) ease-out', painted ? 'opacity-80' : 'opacity-45')}>{description}</p> : <div data-land="desc" className="mt-2 h-2.5 w-2/3 rounded bg-current opacity-10" />}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {[0, 1].map((d) => (
                    <figure key={d} data-land={`photo-${d}`}>
                      <div className={cn('h-20 rounded-md transition-opacity duration-(--duration-drawer)', d < photos ? 'opacity-90' : 'opacity-10')}
                        style={{ background: d < photos ? `linear-gradient(135deg, ${d ? '#6b7f5a' : '#8fa0b3'}, ${accent ?? '#c9b27a'})` : 'currentColor' }} />
                      {captions[d] && <figcaption className="mt-1 truncate text-[10px] opacity-55">{captions[d]}</figcaption>}
                    </figure>
                  ))}
                </div>
                <div data-land="post" className="mt-3 rounded-md px-3 py-2" style={{ backgroundColor: painted ? 'rgba(0,0,0,0.05)' : 'transparent' }}>
                  {post ? (
                    <div className="magic-land"><p className="text-[10px] opacity-55">Latest post</p><p className="truncate text-xs font-semibold">{post}</p></div>
                  ) : <div className="h-6 rounded bg-current opacity-10" />}
                </div>
                <div data-land="footer" className="mt-3 flex justify-between border-t border-current/10 pt-2 text-[10px] opacity-60">
                  <span>{title ? siteName : ''}</span>{donePages.some((p) => /contact/i.test(p.title)) && <span style={linked ? { color: link } : undefined}>Contact</span>}
                </div>
              </div>
            </div>
            {shown === 'ready' && preview && (
              <div className="magic-enter absolute inset-x-0 -bottom-5 flex justify-center">
                <Button asChild>
                  <a href={preview} target="_blank" rel="noreferrer"><ExternalLink /> Open preview</a>
                </Button>
              </div>
            )}
          </div>

          {/* Your style */}
          <div className={cn('hidden self-start rounded-xl border border-white/10 bg-white/[0.03] p-3 transition-opacity duration-(--duration-drawer) ease-out lg:block', shown === 'ready' && 'opacity-50')}>
            <div className="flex items-center justify-between text-[11px]">
              <span className="tracking-wide text-white/50 uppercase">Your style</span>
              {styled && <span className="font-mono text-white/50">static/style.css</span>}
            </div>
            {styled ? (
              <>
                <div data-land="style" className="mt-2 grid grid-cols-4 gap-x-1.5 gap-y-2">
                  {vars.map((v) => (
                    <div key={v.hex} className="magic-land min-w-0">
                      <span className="block h-6 rounded border border-white/10" style={{ backgroundColor: v.hex }} />
                      <p className="mt-0.5 truncate font-mono text-[10px] text-white/70">{v.hex}</p>
                      <p className="truncate text-[10px] text-white/55">{v.name}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {[['Headings', 'Georgia serif', 'ui-serif, Georgia, serif'], ['Body', 'system-ui sans', 'ui-sans-serif, system-ui, sans-serif']].map(([label, face, family]) => (
                    <div key={label} className="magic-land rounded-md bg-white/90 px-2 py-1.5 text-[#1c2430]">
                      <p className="text-xl leading-none" style={{ fontFamily: family }}>Aa</p>
                      <p className="mt-1 text-[10px] font-medium">{label}</p>
                      <p className="text-[10px] opacity-60">{face}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-white/50">Applied to your site</p>
              </>
            ) : (
              <div data-land="style">
                <div className="mt-2 grid grid-cols-4 gap-x-1.5 gap-y-2">
                  {Array.from({ length: 8 }, (_, d) => <span key={d} className="h-6 rounded border border-dashed border-white/15" />)}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {[0, 1].map((d) => <span key={d} className="h-14 rounded-md border border-dashed border-white/15" />)}
                </div>
                <p className="mt-2 text-[11px] text-white/50">{phase >= 2 ? 'Picking colours and fonts' : 'Waiting for the theme'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sitemap */}
        <div className="mt-6 hidden rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 lg:block" aria-label="Sitemap">
          <span className="text-[11px] tracking-wide text-white/50 uppercase">Sitemap</span>
          <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-1">
            <span className="shrink-0 rounded-md border border-white/15 bg-white/[0.04] px-3 py-1.5">
              <span className="block text-xs font-medium">{siteName}</span><span className="block text-[10px] text-white/55">Home</span>
            </span>
            {pages.map((p, i) => (
              <span key={p.title} className="flex shrink-0 items-center gap-2">
                <span className="h-px w-3 bg-white/20" />
                <span className={cn('magic-land relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px]', p.done ? 'border border-white/15 bg-white/[0.06] text-white/85' : 'border border-dashed border-white/25 text-white/55')}>
                  {p.done && <span className="absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full bg-white text-[9px] font-semibold text-[#05060b]">{i + 1}</span>}
                  <span className={cn('size-1.5 rounded-full', p.done ? 'bg-emerald-400' : 'bg-white/25')} />{p.title}
                </span>
              </span>
            ))}
            {post && (
              <span className="magic-land ml-auto flex max-w-56 shrink-0 items-center gap-2 rounded-md border border-white/15 bg-white/[0.04] px-2 py-1.5">
                <span className="size-6 shrink-0 rounded" style={{ background: `linear-gradient(135deg,#8fa0b3,${accent ?? '#c9b27a'})` }} />
                <span className="line-clamp-2 text-[10px] text-white/80">{post}</span>
              </span>
            )}
          </div>
          {(forked || files.length > 0 || photos > 0) && (
            <div className="magic-land mt-2 flex items-center gap-2 overflow-x-auto">
              {Array.from({ length: Math.min(photos, 2) }, (_, d) => (
                <span key={d} className="h-6 w-11 shrink-0 rounded" style={{ background: `linear-gradient(135deg, ${d ? '#6b7f5a' : '#8fa0b3'}, ${accent ?? '#c9b27a'})` }} />
              ))}
              <span className="shrink-0 rounded-md border border-white/10 px-2 py-1 text-[11px]"><b className="font-semibold">Theme</b> <span className="text-white/55">{files.length ? `Made for ${siteName}` : 'Copied from the starter'}</span></span>
              {files.map((f) => <span key={f} className="magic-land shrink-0 rounded bg-white/[0.07] px-1.5 py-0.5 font-mono text-[10px] text-white/75">{f}</span>)}
            </div>
          )}
        </div>

        <div className="mt-auto flex flex-wrap items-center justify-center gap-2 pt-8">
          {shown === 'ready' && preview && (
            <Button asChild className="lg:hidden">
              <a href={preview} target="_blank" rel="noreferrer"><ExternalLink /> Open preview</a>
            </Button>
          )}
          <Button variant={shown === 'working' ? 'ghost' : 'secondary'} className={cn(shown === 'working' && 'text-white/60 hover:text-white')} onClick={onClose} autoFocus>
            {shown === 'ready' ? 'Go to your site' : shown === 'working' ? 'Watch in the chat' : 'Open the chat'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** The admin's motion tokens, read once (WAAPI cannot take var()). */
let tokens: { out: string; inOut: string; dialog: number } | undefined;
function motionTokens() {
  if (!tokens) {
    const css = getComputedStyle(document.documentElement);
    tokens = {
      out: css.getPropertyValue('--ease-out').trim() || 'ease-out',
      inOut: css.getPropertyValue('--ease-in-out').trim() || 'ease-in-out',
      dialog: parseFloat(css.getPropertyValue('--duration-dialog')) || 200,
    };
  }
  return tokens;
}

/** Card trip lengths: ambient, not UI feedback, so longer than the 300 ms rule; page writes burst, so theirs are short. */
const TRIP_MS = { photo: 3000, code: 2000, page: 1550, post: 1550 } as const;

/**
 * One made thing on its trip (07): born at Baxter's eye, it circles the site
 * panel on a shrinking elliptical spiral whose centre drifts onto its landing
 * spot, then dissolves there while a faint amber outline marks the spot.
 */
function FlyCard({ f, wrap, onDone }: { f: Flight; wrap: RefObject<HTMLDivElement | null>; onDone: () => void }) {
  const card = useRef<HTMLDivElement>(null);
  const ring = useRef<HTMLDivElement>(null);
  const done = useRef(onDone);
  done.current = onDone;

  useLayoutEffect(() => {
    const root = wrap.current, el = card.current, pulse = ring.current;
    const rectOf = (key: string) => {
      const r = root?.querySelector(`[data-land="${CSS.escape(key)}"]`)?.getBoundingClientRect();
      return r && r.width > 0 ? r : undefined;
    };
    const live: Animation[] = [];
    try {
      const eye = rectOf('eye'), site = rectOf('site');
      const land = rectOf(f.target) ?? site;
      if (!root || !el || !pulse || !eye || !site || !land) { done.current(); return; }
      const box = root.getBoundingClientRect();
      const mid = (r: DOMRect) => ({ x: r.left - box.left + r.width / 2, y: r.top - box.top + r.height / 2 });
      const E = mid(eye), C = mid(site), L = mid(land);
      const { out, inOut, dialog } = motionTokens();
      const at = (x: number, y: number) => `translate(${(x - 32).toFixed(1)}px, ${(y - 22).toFixed(1)}px)`;

      let ms: number;
      if (reducedMotion()) {
        // No travel: it fades in on its spot, holds, and dissolves into it.
        el.style.transform = at(L.x, L.y);
        ms = dialog * 3;
        live.push(el.animate([{ opacity: 0 }, { opacity: 0.4, offset: 0.33 }, { opacity: 0.4, offset: 0.67 }, { opacity: 0 }], { duration: ms, easing: 'linear' }));
      } else {
        const turns = f.twice ? 1.8 : 0.8, rx = site.width * 0.62, ry = Math.max(40, C.y - E.y), frames: Keyframe[] = [];
        for (let j = 0; j <= 72; j++) {
          const u = j / 72, e = u * u * (3 - 2 * u), a = -Math.PI / 2 + u * turns * Math.PI * 2, sh = (1 - u) ** 0.7; // wide round the panel, tightening late
          frames.push({ offset: u, transform: at(C.x + (L.x - C.x) * e + rx * sh * Math.cos(a), C.y + (L.y - C.y) * e + ry * sh * Math.sin(a)) });
        }
        ms = TRIP_MS[f.kind] + (f.twice ? (f.kind === 'page' || f.kind === 'post' ? 300 : 450) : 0);
        live.push(el.animate(frames, { duration: ms, easing: inOut, fill: 'forwards' }));
        // Appear and dissolve keep their own short clocks (07): the path easing must not stretch them.
        live.push(el.animate([{ opacity: 0, scale: 0.6 }, { opacity: 0.4, scale: 1 }], { duration: 300, easing: out, fill: 'forwards' }));
        live.push(el.animate([{ opacity: 0.4, scale: 1 }, { opacity: 0, scale: 0.9 }], { delay: ms - 120, duration: 150, easing: out, fill: 'forwards' }));
      }
      live[live.length - 1].finished
        .then(() => {
          const now = rectOf(f.target) ?? rectOf('site'); // measured at landing: the nav and title reflow mid-trip
          const b = root.getBoundingClientRect();
          if (now) Object.assign(pulse.style, { left: `${now.left - b.left - 4}px`, top: `${now.top - b.top - 3}px`, width: `${now.width + 8}px`, height: `${now.height + 6}px` });
          const p = pulse.animate([{ opacity: 0 }, { opacity: 0.55, offset: 0.3 }, { opacity: 0 }], { duration: 700, easing: out });
          live.push(p);
          return p.finished;
        })
        .then(() => done.current(), () => { /* cancelled on unmount */ });
    } catch {
      done.current(); // a flight that cannot animate must still free its slot under the cap
    }
    return () => live.forEach((a) => a.cancel());
  }, [f, wrap]);

  return (
    <>
      <div ref={ring} className="absolute rounded-md border border-amber-400 opacity-0" />
      <div ref={card} className={cn('absolute left-0 top-0 h-11 w-16 overflow-hidden rounded-[7px] border border-white/25 bg-[#28282d] opacity-0', f.kind === 'photo' ? 'p-[3px]' : 'px-1.5 py-[5px]')}>
        {f.kind === 'photo' ? (
          <i className={cn('block h-full rounded bg-linear-135', f.alt ? 'from-[#6f8a6a] to-[#d9c27a]' : 'from-[#8aa1b5] to-[#c9b98f]')} />
        ) : (
          <>
            <small className="block text-[8px] tracking-wide text-white/60 uppercase">
              {f.kind === 'code' && <span className="text-primary">{'</> '}</span>}{f.kind === 'code' ? 'Theme' : f.kind === 'post' ? 'Post' : 'Page'}
            </small>
            <b className={cn('mt-px block truncate font-semibold text-white', f.kind === 'code' ? 'font-mono text-[9px]' : 'text-[10px]')}>{f.label}</b>
            {f.kind !== 'code' && <div className="mt-[3px] h-[3px] w-3/4 rounded-sm bg-white/25" />}
          </>
        )}
      </div>
    </>
  );
}

/** A small pixel wizard hat; tips once when the preview is ready. */
function WizardHat({ tip }: { tip: boolean }) {
  return (
    <svg viewBox="0 0 12 10" className={cn('absolute -top-6 left-1/2 h-7 w-8 -translate-x-1/2', tip && 'magic-tip')} shapeRendering="crispEdges" aria-hidden="true">
      <path d="M5 0h2v1h1v2h1v2h1v2h2v2H0V7h2V5h1V3h1V1h1z" fill="#5b3fd6" />
      <path d="M0 8h12v1H0z" fill="#3a2a8f" />
      <path d="M6 3h1v1H6zM4 5h1v1H4z" fill="#ffd54a" />
    </svg>
  );
}

/** A handful of pixel sparkles bursting from Baxter's paw. */
function Sparkles() {
  const dirs = [[-34, -18], [30, -24], [-26, 20], [36, 14], [0, -38], [-40, 2]];
  return (
    <span className="pointer-events-none absolute left-1/2 top-1/2" aria-hidden="true">
      {dirs.map(([dx, dy], i) => (
        <span
          key={i}
          className="magic-spark absolute size-1.5 bg-[#ffd54a]"
          style={{ '--dx': `${dx}px`, '--dy': `${dy}px`, animationDelay: `${i * 30}ms` } as CSSProperties}
        />
      ))}
    </span>
  );
}
